// TMOS Action Template Service
// Date: December 9, 2025
// Purpose: Frontend service for action templates API
//
// Features:
// - Get platform and tenant templates
// - Create/update/delete tenant templates
// - Apply templates to create actions

import api from './api';

export interface ActionTemplate {
  id: string;
  tenant_id?: string | null;
  name: string;
  description?: string | null;
  entity_type: string;
  template_data: ActionTemplateDefinition[];
  is_platform_template: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActionTemplateDefinition {
  title: string;
  description?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  activity_category_id?: string;
  due_date_offset_days?: number; // Days from today
  assigned_user_ids?: string[];
}

export interface CreateActionTemplateRequest {
  name: string;
  description?: string;
  entity_type: string;
  template_data: ActionTemplateDefinition[];
}

export interface UpdateActionTemplateRequest {
  name?: string;
  description?: string;
  template_data?: ActionTemplateDefinition[];
  is_active?: boolean;
}

/**
 * ActionTemplateService class
 * 
 * Provides methods for managing and applying action templates.
 */
class ActionTemplateService {
  /**
   * Get templates for current tenant (platform + tenant templates)
   */
  async getTemplates(entityType?: string): Promise<ActionTemplate[]> {
    const params = new URLSearchParams();
    if (entityType) {
      params.append('entity_type', entityType);
    }
    
    const queryString = params.toString();
    const response = await api.get(`/action-templates${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get template by ID
   */
  async getTemplateById(id: string): Promise<ActionTemplate> {
    const response = await api.get(`/action-templates/${id}`);
    return response.data.data;
  }

  /**
   * Create template (tenant admin only)
   */
  async createTemplate(data: CreateActionTemplateRequest): Promise<ActionTemplate> {
    const response = await api.post('/action-templates', data);
    return response.data.data;
  }

  /**
   * Update template (tenant admin only)
   */
  async updateTemplate(id: string, data: UpdateActionTemplateRequest): Promise<ActionTemplate> {
    const response = await api.put(`/action-templates/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete template (tenant admin only)
   */
  async deleteTemplate(id: string): Promise<void> {
    await api.delete(`/action-templates/${id}`);
  }

  /**
   * Apply template to create actions
   */
  async applyTemplate(
    templateId: string,
    contextType?: string,
    contextId?: string
  ): Promise<any[]> {
    const response = await api.post(`/action-templates/${templateId}/apply`, {
      context_type: contextType,
      context_id: contextId
    });
    return response.data.data || [];
  }
}

// Export singleton instance
export const actionTemplateService = new ActionTemplateService();
export default actionTemplateService;

